import { Dispatch, SetStateAction, useEffect } from 'react'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { dateToString, parseToDate } from 'lib/helper'
import { subMinutes } from 'date-fns'
import { ConfirmStock, StockType } from 'types/stock'
import { StoreCalendarType } from 'types/storeCalendar'
import { SpecialHolidayType } from 'types/calendar'
import { ScheduleType } from 'types/schedule'
import { BreadType } from 'types/bread'
import isSameDay from 'date-fns/isSameDay'
import { FirebaseDocRefType, FirebaseTimestamp } from 'firebase/index'
const daysOfWeek = new DaysOfWeek()

const useConfirmStocks = (
  breads: BreadType[],
  schedules: ScheduleType[],
  selectedDates: Date[],
  setConfirmStocks: Dispatch<SetStateAction<ConfirmStock[]>>,
  specialHolidays: SpecialHolidayType[],
  stocks: StockType[],
  storeCalendars: StoreCalendarType[],
  storeName: string,
  storeRef: FirebaseDocRefType
) => {
  return useEffect(() => {
    const confirmStocks: ConfirmStock[] = []

    for (let i = 0; i < breads.length; i = (i + 1) | 0) {
      const bread = breads[i]
      const stocksOfBread = stocks.filter((stock) => bread.bread_ref?.id === stock.bread_ref.id)
      const schedulesOfBread = schedules.filter((schedule) => bread.bread_ref?.id === schedule.bread_ref.id)
      const addedStocks: StockType[] = []
      const deletedStocks: StockType[] = []
      const updatedStocks: StockType[] = []

      // 選択された日付から新規登録される在庫を判定する
      for (let j = 0; j < selectedDates.length; j = (j + 1) | 0) {
        const selectedDate = selectedDates[j]
        const date = dateToString(selectedDate)
        const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(selectedDate, specialHolidays)
        const temporaryOpen = storeCalendars.find(
          (storeCalendar) => storeCalendar.date === date && storeCalendar.status === 'open'
        )
        const defaultDayOfWeek = temporaryOpen?.default_day_of_week || dayOfWeek
        const stock = stocksOfBread.find((stock) => isSameDay(parseToDate(stock.date), selectedDate))
        const schedule = schedulesOfBread.find((schedule) => schedule.days_of_week.includes(defaultDayOfWeek))
        // 受渡可能曜日ではないまたは個別設定日なのでスキップ
        if (!schedule) continue
        // 受渡可能日が未設定であれば追加
        if (!stock) {
          // 受渡解禁時刻ごとにstockを追加
          schedule.default_stocks.map((defaultStock) => {
            const deadline = FirebaseTimestamp.fromDate(subMinutes(selectedDate, schedule.hours_to_deadline * 60))
            const stockRef = storeRef
              .collection('store_stocks')
              .doc(`stock_${schedule.type}_${schedule.schedule_ref?.id}-${date}_${defaultStock.pickupable_time}`)
            const stockValue: StockType = {
              bread_ref: schedule.bread_ref,
              bread_name: bread.name,
              date: date,
              choices: schedule.choices,
              deadline: deadline,
              image_url: bread.image_urls[0] || '',
              last_notified_time: null,
              maximum_order: schedule.maximum_order,
              note: schedule.note,
              options: schedule.options,
              pickupable_time: defaultStock.pickupable_time,
              price: bread.price,
              stock: defaultStock.stock,
              stock_ref: stockRef,
              store_name: storeName,
              store_ref: storeRef,
              type: schedule.type,
            }
            addedStocks.push(stockValue)
          })
        }
      }

      for (let j = 0; j < stocksOfBread.length; j = (j + 1) | 0) {
        const stock = stocksOfBread[j]
        const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(parseToDate(stock.date), specialHolidays)
        const temporaryOpen = storeCalendars.find(
          (storeCalendar) => storeCalendar.date === stock.date && storeCalendar.status === 'open'
        )
        const defaultDayOfWeek = temporaryOpen?.default_day_of_week || dayOfWeek
        const schedule = schedulesOfBread.find((schedule) => schedule.days_of_week.includes(defaultDayOfWeek))
        // 受渡可能曜日でなければ臨時営業日なので上書き・削除しない
        if (!schedule) continue
        // 選択された日付に存在するものは上書き
        if (selectedDates.some((selectedDate) => isSameDay(selectedDate, parseToDate(stock.date)))) {
          // 受渡解禁時刻ごとにstockを追加
          schedule.default_stocks.map((defaultStock) => {
            const deadline = FirebaseTimestamp.fromDate(
              subMinutes(parseToDate(stock.date), schedule.hours_to_deadline * 60)
            )
            const stockRef = storeRef
              .collection('store_stocks')
              .doc(`stock_${schedule.type}_${schedule.schedule_ref?.id}-${stock.date}_${defaultStock.pickupable_time}`)
            const stockValue: StockType = {
              bread_ref: schedule.bread_ref,
              bread_name: bread.name,
              date: stock.date,
              choices: schedule.choices,
              deadline: deadline,
              image_url: bread.image_urls[0] || '',
              last_notified_time: null,
              maximum_order: schedule.maximum_order,
              note: schedule.note,
              options: schedule.options,
              pickupable_time: defaultStock.pickupable_time,
              price: bread.price,
              stock: defaultStock.stock,
              stock_ref: stockRef,
              store_name: storeName,
              store_ref: storeRef,
              type: schedule.type,
            }
            // sacri stationの場合、販売元店舗のrefを追加する
            if (typeof schedule.manufacturer_ref !== 'undefined') {
              stockValue.manufacturer_ref = schedule.manufacturer_ref
            }
            updatedStocks.push(stockValue)
            // 個別設定で受渡解禁時刻が異なる場合にIDが変わるため削除する
            if (stock.stock_ref && stock.stock_ref.id !== stockRef.id) {
              deletedStocks.push(stock)
            }
          })
        } else {
          // 選択された日付に存在しなければ削除
          deletedStocks.push(stock)
        }
      }

      confirmStocks.push({
        addedStocks,
        bread,
        deletedStocks,
        updatedStocks,
      })
    }
    setConfirmStocks([...confirmStocks])
  }, [breads, schedules, selectedDates, setConfirmStocks, specialHolidays, stocks, storeCalendars, storeName, storeRef])
}

export default useConfirmStocks
